/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { DebtorOrderDto } from '../models/DebtorOrderDto';
import type { OrderConfigurationsDto } from '../models/OrderConfigurationsDto';
import type { OrderDetailDto } from '../models/OrderDetailDto';
import type { OrderDto } from '../models/OrderDto';
import type { OrderListDto } from '../models/OrderListDto';
import type { OrdersDto } from '../models/OrdersDto';
import type { OrderStatus } from '../models/OrderStatus';
import type { PaginationQueryDto } from '../models/PaginationQueryDto';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class OrderService {

    /**
     * @returns OrderDetailDto The details of an order for given debtor.
     * @throws ApiError
     */
    public static getShowOrder({
        locale,
        salesOrderNumber,
    }: {
        /**
         * Locale
         */
        locale: string,
        salesOrderNumber: string,
    }): CancelablePromise<OrderDetailDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/orders/{salesOrderNumber}',
            path: {
                'salesOrderNumber': salesOrderNumber,
            },
            query: {
                'locale': locale,
            },
            errors: {
                401: `Unauthorized`,
                404: `Order not found.`,
            },
        });
    }

    /**
     * @returns any Order successfully created.
     * @throws ApiError
     */
    public static putSaveOrder({
        salesOrderNumber,
        requestBody,
    }: {
        salesOrderNumber: string,
        /**
         * Request body for creating and updating an order.
         */
        requestBody: OrderDto,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/orders/{salesOrderNumber}',
            path: {
                'salesOrderNumber': salesOrderNumber,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Order creation failed.`,
            },
        });
    }

    /**
     * @returns any Order succesfully deleted.
     * @throws ApiError
     */
    public static deleteDeleteOrder({
        salesOrderNumber,
    }: {
        salesOrderNumber: string,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/orders/{salesOrderNumber}',
            path: {
                'salesOrderNumber': salesOrderNumber,
            },
            errors: {
                401: `Unauthorized`,
                404: `Order not found.`,
            },
        });
    }

    /**
     * @returns any Order is found.
     * @throws ApiError
     */
    public static headCheckOrder({
        salesOrderNumber,
        zipCode,
        email,
    }: {
        salesOrderNumber: string,
        /**
         * Zip code
         */
        zipCode?: string,
        /**
         * Email
         */
        email?: string,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'HEAD',
            url: '/api/v1/orders/{salesOrderNumber}',
            path: {
                'salesOrderNumber': salesOrderNumber,
            },
            query: {
                'zipCode': zipCode,
                'email': email,
            },
            errors: {
                401: `Unauthorized`,
                404: `Order not found.`,
            },
        });
    }

    /**
     * @returns any
     * @throws ApiError
     */
    public static postCreateOrder({
        requestBody,
    }: {
        requestBody: DebtorOrderDto,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/orders',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any
     * @throws ApiError
     */
    public static postOrderConfigurations({
        locale,
        salesOrderNumber,
        requestBody,
        country,
        debtor,
        validateStock,
    }: {
        /**
         * Locale
         */
        locale: string,
        salesOrderNumber: string,
        /**
         * Request body for creating an order for configurations.
         */
        requestBody: OrderConfigurationsDto,
        /**
         * Country code
         */
        country?: string,
        /**
         * Debtor code
         */
        debtor?: string,
        /**
         * Toggle to enable stock validation
         */
        validateStock?: 0 | 1,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/orders/{salesOrderNumber}/configurations',
            path: {
                'salesOrderNumber': salesOrderNumber,
            },
            query: {
                'locale': locale,
                'country': country,
                'debtor': debtor,
                'validateStock': validateStock,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns OrdersDto Returns a collection of orders.
     * @throws ApiError
     */
    public static getShowOrders({
        debtor,
        page,
        sort,
        status,
        updatedAfter,
        updatedBefore,
        reference,
        productionOrderNumber,
        searchString,
    }: {
        /**
         * Debtor code
         */
        debtor: string,
        /**
         * Pagination parameter
         */
        page: PaginationQueryDto,
        /**
         * Fieldname (append - for descending order)
         */
        sort?: string,
        /**
         * status
         */
        status?: OrderStatus,
        /**
         * Filter orders after updated timestamp
         */
        updatedAfter?: string,
        /**
         * Filter orders before updated timestamp
         */
        updatedBefore?: string,
        /**
         * Filter orders on order reference
         */
        reference?: string,
        /**
         * Filter orders on production order number
         */
        productionOrderNumber?: string,
        /**
         * Search query string
         */
        searchString?: string,
    }): CancelablePromise<OrdersDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/orders/',
            query: {
                'debtor': debtor,
                'sort': sort,
                'page': page,
                'status': status,
                'updatedAfter': updatedAfter,
                'updatedBefore': updatedBefore,
                'reference': reference,
                'productionOrderNumber': productionOrderNumber,
                'searchString': searchString,
            },
        });
    }

    /**
     * @returns OrderListDto The collection of orders for given debtor.
     * @throws ApiError
     */
    public static getShowOrdersForDebtor({
        page,
        pageSize,
        debtorCode,
        searchString,
        status,
    }: {
        /**
         * Page number
         */
        page: number,
        /**
         * Page size number
         */
        pageSize: number,
        debtorCode: string,
        /**
         * Search query string
         */
        searchString?: string,
        /**
         * status
         */
        status?: OrderStatus,
    }): CancelablePromise<OrderListDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/debtors/{debtorCode}/orders',
            path: {
                'debtorCode': debtorCode,
            },
            query: {
                'page': page,
                'pageSize': pageSize,
                'searchString': searchString,
                'status': status,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }

}
